import { useState } from 'react'
import type { RoomInfoDto } from '../../types/community'
import { Card } from '../ui/Card'
import { Pill } from '../ui/Pill'
import { SectionLabel } from '../ui/SectionLabel'
import { CopyButton } from '../ui/CopyButton'
import { useToast } from '../../contexts/ToastContext'
import { cn } from '../../lib/cn'

interface RoomInfoCardProps {
  info: RoomInfoDto
  rules: string[]
}

function InfoRow({ label, value, children }: { label: string; value: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-line-row py-3 first:border-t-0">
      <div className="min-w-0">
        <SectionLabel>{label}</SectionLabel>
        <div className="mt-1 truncate font-mono text-[13px] text-ink">{value}</div>
      </div>
      {children}
    </div>
  )
}

/** Room info card (COTC_Rooms R4 / DESIGN §3.5): tint bar, identity, copyable rows, rule chips. */
export function RoomInfoCard({ info, rules }: RoomInfoCardProps) {
  const { flash } = useToast()
  const [showPassword, setShowPassword] = useState(false)
  const hasPassword = !!info.password && info.password.length > 0

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="h-[6px] w-full" style={{ background: info.tint || '#b9704f' }} />
      <div className="px-6 py-5">
        <SectionLabel>{info.game}</SectionLabel>
        <h2 className="mt-1 font-serif text-[24px] font-medium leading-tight text-ink">{info.name}</h2>
        <p className="mt-1 text-[13px] text-ink-muted">{info.hostLine}</p>

        <div className="mt-4">
          <InfoRow label="Room code" value={info.code}>
            <CopyButton value={info.code} what="Room code" />
          </InfoRow>

          {info.serverAddress && (
            <InfoRow label="Server / address" value={info.serverAddress}>
              <CopyButton value={info.serverAddress} what="Server address" />
            </InfoRow>
          )}

          <div className="flex items-center justify-between gap-3 border-t border-line-row py-3">
            <div className="min-w-0">
              <SectionLabel>Password</SectionLabel>
              <div className="mt-1 truncate font-mono text-[13px] text-ink">
                {!hasPassword ? 'No password' : showPassword ? info.password : '•••••••••'}
              </div>
            </div>
            <div className="flex flex-none items-center gap-2">
              {hasPassword && (
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="rounded-btn border border-line bg-paper-surface px-3 py-2 font-mono text-[11px] uppercase tracking-label text-ink-muted transition-colors hover:border-terra hover:text-terra-deep"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              )}
              {hasPassword ? (
                <CopyButton value={info.password as string} what="Room password" />
              ) : (
                <button
                  type="button"
                  onClick={() => flash('This room has no password.')}
                  className="rounded-btn border border-line bg-paper-surface px-3 py-2 font-mono text-[11px] uppercase tracking-label text-ink-muted transition-colors hover:border-terra hover:text-terra-deep"
                >
                  Copy
                </button>
              )}
            </div>
          </div>
        </div>

        {info.instructions && info.instructions.trim().length > 0 && (
          <div className="mt-5">
            <SectionLabel>How to join in the game</SectionLabel>
            <p className="mt-2 whitespace-pre-line text-[13.5px] leading-relaxed text-ink-muted">
              {info.instructions}
            </p>
          </div>
        )}

        {rules.length > 0 && (
          <div className="mt-5">
            <SectionLabel>House rules</SectionLabel>
            <div className={cn('mt-2 flex flex-wrap gap-2')}>
              {rules.map((rule, i) => (
                <Pill key={i}>{rule}</Pill>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}
